import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Grid, Stack, Typography, TextField, Button,
  Select, MenuItem, FormControl, FormHelperText, IconButton, CircularProgress
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { useIsMobile } from '@/hooks/Responsive'
import CloseIcon from '@mui/icons-material/Close'
import { fetchMyCourseComboList, insertMyQuestionData, getTsid } from '@/api/mypage'
import { sendEmailUtil, uploadFileLmsSend, uploadMultiFileUtil } from '@/api/util'
import { useAuth } from '@/components/AuthContext'
import { getTable } from '@/utils/ChkUtil'

export default function MyInquiryWrite() {
  const { isMobile } = useIsMobile()
  const navigate = useNavigate()

  const { user } = useAuth();

  const [form, setForm] = useState({
    subCategory: '',
    lecture: '',
    title: '',
    content: '',
  })

  const [files, setFiles] = useState([])
  const [lectures, setLectures] = useState([])
  const [lecturesLoading, setLecturesLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pkId, setPkId] = useState('')
  const [attachUuid, setAttachUuid] = useState(null)

  const SUBCATEGORY_OPTIONS = ['강좌문의', '콘텐츠문의']

  useEffect(() => {
    const getLectures = async () => {
      setLecturesLoading(true)
      try {
        const response = await fetchMyCourseComboList({ params: { accountId: user.accountId } })
        setLectures(response.data || [])
      } catch (error) {
        console.error("Failed to fetch lectures:", error)
        setLectures([])
      } finally {
        setLecturesLoading(false)
      }
    }
    getLectures()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubCategoryChange = (e) => {
    const value = e.target.value
    setForm((prev) => ({
      ...prev,
      subCategory: value,
      lecture: value === '강좌문의' ? prev.lecture : '', 
    }))
  }

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || [])
    setFiles((prev) => [...prev, ...newFiles])
  }

  const handlePreview = (file) => {
    const fileURL = URL.createObjectURL(file)
    window.open(fileURL, '_blank')
  }

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  //파일업로드 기능 구현
  const handleUpload = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    let fileUuid = null
    if (files && files.length > 0) {
      try {
        const tsid = await getTsid()
        if (tsid) {
          setPkId(tsid.data);
          const fileFormData = new FormData()
          fileFormData.append('files', files[0])
          fileFormData.append('tableName', getTable(form.lecture ?'CRS':'SPC'))
          fileFormData.append('tablePkId', tsid.data)

          const response = await uploadFileLmsSend(fileFormData)
          fileUuid = response?.data
          setAttachUuid(fileUuid)
        } else {
          throw new Error('TSID를 가져오는 데 실패했습니다.')
        }
      } catch (error) {
        console.error('File upload failed:', error)
        alert('파일 업로드 중 오류가 발생했습니다.')
        setIsSubmitting(false)
        return
      }
    }

    handleSubmit(e, fileUuid)
  }

  const handleSubmit = async (e, fileUuid) => {
    // This function is now called by handleUpload, so preventDefault isn't needed if we assume e is just passed.
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault()
    }

    // if called directly without files
    if (!isSubmitting) setIsSubmitting(true)

    const formData = new FormData()
    formData.append('bulletinId', pkId)
    formData.append('eduLectureId', form.lecture)
    formData.append('bbsType', form.subCategory === '강좌문의' && form.lecture ? 'lecture' : 'content')
    formData.append('bulletinTitle', form.title)
    formData.append('bulletinSubstatnce', form.content)
    formData.append('accountId', user.accountId)
    if (fileUuid) {
      formData.append('attachFileUuid', fileUuid)
    }

    try {
      const response = await insertMyQuestionData(formData)
      if (response?.data !== '0') {
        alert('문의가 등록되었습니다.')
        navigate('/mypage/inquiry')
      } else {
        alert('문의 등록에 실패했습니다.')
        setIsSubmitting(false)
      }
    } catch (error) {
      console.error('Failed to submit inquiry:', error)
      alert('문의 등록 중 오류가 발생했습니다.')
      setIsSubmitting(false)
    }
  }

  const isLectureEnabled = form.subCategory === '강좌문의'

  return (
    <Box
      component="form"
      onSubmit={handleUpload}
      // onSubmit={handleMailSend}
    >

      {/* 소분류 + 강좌 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography component="label" htmlFor="category" fontWeight={500} sx={{ mb: 0.5 }}>
            분류 <Typography component="span" color="error">*</Typography>
            <Box component="span" sx={visuallyHidden}>필수 입력</Box>
          </Typography>
          <FormControl size="small" fullWidth required>
            <Select
              name="subCategory"
              value={form.subCategory}
              onChange={handleSubCategoryChange}
              displayEmpty
              inputProps={{
                'id': 'category',
                'labelId': 'category',
                'aria-label': '분류 선택',
              }}
            >
              <MenuItem value="">분류를 선택해 주세요</MenuItem>
              {SUBCATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography component="label" htmlFor="lecture" fontWeight={500} sx={{ mb: 0.5 }}>
            강좌 {isLectureEnabled && (
              <Typography component="span" color="error">*</Typography>
            )}
            {isLectureEnabled && (
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            )}
          </Typography>
          <FormControl size="small" fullWidth disabled={!isLectureEnabled || lecturesLoading} required={isLectureEnabled}>
            <Select
              name="lecture"
              value={form.lecture}
              onChange={handleChange}
              displayEmpty
              inputProps={{
                'id': 'lecture',
                'labelId': 'lecture',
                'aria-label': '강좌 선택',
              }}
            >
              <MenuItem value="">
                {lecturesLoading ? '강좌 목록을 불러오는 중...' : '강좌를 선택해 주세요'}
              </MenuItem>
              {lectures.map((opt) => (
                <MenuItem key={opt.code} value={opt.code}>
                  {opt.codeNm}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* 제목 */}
      <Box sx={{ mb: 3 }}>
        <Typography component="label" htmlFor="title" fontWeight={500} sx={{ mb: 0.5 }}>
          제목 <Typography component="span" color="error">*</Typography>
          <Box component="span" sx={visuallyHidden}>필수 입력</Box>
        </Typography>
        <TextField
          fullWidth
          id="title"
          placeholder="제목을 입력해 주세요"
          name="title"
          value={form.title}
          onChange={handleChange}
          size="small"
          required
        />
      </Box>

      {/* 내용 */}
      <Box sx={{ mb: 3 }}>
        <Typography component="label" htmlFor="content" fontWeight={500} sx={{ mb: 0.5 }}>
          내용 <Typography component="span" color="error">*</Typography>
          <Box component="span" sx={visuallyHidden}>필수 입력</Box>
        </Typography>
        <TextField
          id="content"
          multiline
          minRows={6}
          fullWidth
          placeholder="내용을 입력해 주세요"
          name="content"
          value={form.content}
          onChange={handleChange}
          required
        />
      </Box>

      {/* 첨부파일 */}
      <Box sx={{ mb: 3 }}>
        <Typography fontWeight={500} sx={{ mb: 0.5 }}>
          첨부파일
        </Typography>

        {/* 칩 영역 + 파일 선택 버튼 */}
        <Stack 
          direction={isMobile ? 'column' : 'row'} 
          alignItems="stretch"
          sx={{ gap: 1 }}
        >
          
          {/* 파일 칩들 */}
          <Stack
            direction="row"
            sx={{ 
              flexGrow: 1,
              flexWrap: 'wrap',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.100',
              px: 2,
              py: 1,
              borderRadius: 2,
              minHeight: '48px',
              gap: 1,
            }} 
          >
            {files.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                }}
                onClick={() => handlePreview(file)}
              >
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(12)})` : '160px',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {file.name}
                </Typography>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFile(idx)
                  }}
                  sx={{
                    color: 'grey.dark',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>

          {/* 파일 선택 버튼 */}
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            component="label"
            sx={{ flexShrink: 0 }}
          >
            파일선택
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.hwp,.hwpx,.ppt,.pptx,.doc,.docx"
            />
          </Button>
        </Stack>

        <FormHelperText sx={{ mt: 0.5 }}>
          첨부파일은 pdf, jpg, jpeg, png, hwp, hwpx, ppt, pptx, doc, docx 형식만 가능합니다.
        </FormHelperText>
      </Box>


      {/* 안내문 */}
      <Typography 
        variant="body2"
        sx={{
          display: 'flex',
          gap: 1, 
          '&::before': {
            content: "''",
            display: 'inline-block',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            bgcolor: 'grey.600',
            mt: 1,
          },
        }}
      >
        강의 내용과 관련 없는 문의에는 답변을 드리지 않을 수 있는 점 참고해 주세요.
      </Typography>

      {/* 버튼 */}
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 5 }}>
        <Button 
          variant="outlined" 
          size="large" 
          color="grey"
          sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 3 : 0 }}
          onClick={() => navigate(-1)}
          disabled={isSubmitting}
        >
          취소
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          size="large"
          sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 3 : 0 }}
          disabled={isSubmitting || lecturesLoading}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : '등록'}
        </Button>
      </Stack>
    </Box>
  )
}